import React from 'react';
import GuidelineSection from '../components/GuidelineSection';
import SubSection from '../components/SubSection';
import Table from '../components/Table';
import TableOfContents from '../components/TableOfContents';
const WholeHealth: React.FC = () => {
  const tocItems = [{
    id: 'introduction',
    title: 'Introduction',
    level: 1
  }, {
    id: 'centers-services',
    title: 'Centers and Services',
    level: 1
  }, {
    id: 'nutrition',
    title: 'Nutrition',
    level: 1
  }, {
    id: 'exercise',
    title: 'Exercise',
    level: 1
  }, {
    id: 'stress-reduction',
    title: 'Stress Reduction',
    level: 1
  }, {
    id: 'emotional-wellbeing',
    title: 'Emotional Wellbeing',
    level: 1
  }, {
    id: 'social-connection',
    title: 'Social Connection',
    level: 1
  }, {
    id: 'mindset',
    title: 'Mindset',
    level: 1
  }];
  return <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-519">
      <div className="lg:col-span-3" data-id="element-520">
        <GuidelineSection id="whole-health" title="4. WHOLE HEALTH (Integrative Oncology and Palliative Care)" data-id="element-521">
          <SubSection id="introduction" title="4.1 Introduction" data-id="element-522">
            <p className="mb-4" data-id="element-523">
              <strong data-id="element-524">Purpose:</strong> Up-regulate your immune system and
              down-regulate the tumor microenvironment through evidence-based
              lifestyle interventions.
            </p>
            <p className="mb-4" data-id="element-525">
              Integrative oncology combines conventional cancer treatment with
              complementary approaches that have been researched and proven safe
              and effective. These interventions can:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-526">
              <li data-id="element-527">
                Reduce treatment-related side effects (fatigue, nausea, pain)
              </li>
              <li data-id="element-528">Improve quality of life and physical functioning</li>
              <li data-id="element-529">Support immune system function</li>
              <li data-id="element-530">Enhance treatment outcomes</li>
            </ul>
            <p className="mb-4" data-id="element-531">
              <strong data-id="element-532">Taxonomy:</strong> Framework drawn from Society for
              Integrative Oncology (SIO) and Center for Cancer Lifestyle
              Management (CCLM) guidelines.
            </p>
          </SubSection>
          <SubSection id="centers-services" title="4.2 Centers and Services" data-id="element-533">
            <p className="mb-4" data-id="element-534">
              Table: Integrative Oncology Centers and Services
            </p>
            <Table headers={['Name', 'Type', 'Location', 'Services', 'Contact']} rows={[['MD Anderson Integrative Medicine Center', 'Comprehensive center', 'Houston, TX', 'Acupuncture, nutrition, exercise, mind-body', 'mdanderson.org/integrativemed'], ['Mayo Clinic Integrative Medicine', 'Comprehensive center', 'Multiple locations', 'Full integrative services', 'mayoclinic.org'], ['Memorial Sloan Kettering Integrative Medicine', 'Comprehensive center', 'New York, NY', 'All modalities', 'mskcc.org/integrativemedicine'], [<span className="font-semibold" data-id="element-536">Online/Remote Services</span>, '', '', '', ''], ['Cancer Lifeline', 'Telehealth support', 'Virtual (Seattle-based)', 'Nutrition counseling, support groups', 'cancerlifeline.org'], ['Integrative Oncology Essentials', 'Online courses', 'Virtual', 'Self-paced education', 'integrativeonc.org']]} data-id="element-535" />
          </SubSection>
          <SubSection id="nutrition" title="4.3 Nutrition" data-id="element-537">
            <p className="mb-4" data-id="element-538">
              <strong data-id="element-539">Overview:</strong> Nutrition plays a critical role in
              supporting your body during cancer treatment. Key goals include:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-540">
              <li data-id="element-541">
                Maintaining adequate calorie and protein intake to prevent
                cachexia
              </li>
              <li data-id="element-542">
                Managing treatment side effects (nausea, taste changes,
                constipation)
              </li>
              <li data-id="element-543">Supporting immune function</li>
              <li data-id="element-544">Optimizing body composition</li>
            </ul>
            <p className="font-medium mb-2" data-id="element-545">Options:</p>
            <p className="font-medium mb-2" data-id="element-546">Foods to Embrace:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-547">
              <li data-id="element-548">
                Protein-rich foods: Lean meats, fish, eggs, legumes, Greek
                yogurt (1.2-2.0g/kg body weight daily)
              </li>
              <li data-id="element-549">
                Colorful vegetables: Cruciferous vegetables (broccoli,
                cauliflower), leafy greens, tomatoes
              </li>
              <li data-id="element-550">
                Anti-inflammatory foods: Fatty fish (omega-3s), berries,
                turmeric, green tea
              </li>
              <li data-id="element-551">
                Whole grains: Brown rice, quinoa, oats for sustained energy
              </li>
            </ul>
            <p className="font-medium mb-2" data-id="element-552">Foods to Limit:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-553">
              <li data-id="element-554">Processed meats and highly processed foods</li>
              <li data-id="element-555">Excessive sugar and refined carbohydrates</li>
              <li data-id="element-556">
                Alcohol (increases cancer risk and interferes with treatment)
              </li>
              <li data-id="element-557">
                Supplements that may interfere with treatment (high-dose
                antioxidants during chemotherapy/radiation)
              </li>
            </ul>
            <p className="font-medium mb-2" data-id="element-558">Evidence-Based Dietary Patterns:</p>
            <div className="bg-yellow-50 p-4 rounded-lg mb-6" data-id="element-559">
              <h4 className="font-semibold text-yellow-800 mb-2" data-id="element-560">
                Important Clarification on Mediterranean Diet:
              </h4>
              <p data-id="element-561">
                Current ASCO/SIO guidelines do not specifically recommend
                Mediterranean diet for patients undergoing cancer treatment. The
                ASCO 2022 guideline explicitly states "evidence for other
                dietary and weight loss interventions during cancer treatment
                was very limited" and found insufficient evidence to recommend
                specific dietary patterns including Mediterranean diet during
                active treatment.[59] While Mediterranean diet receives positive
                discussion for cancer prevention and survivorship in general
                medical literature, it lacks specific recommendation in ASCO/SIO
                clinical practice guidelines for patients undergoing treatment.
                The only specific diet recommendation in current guidelines is
                negative—neutropenic diets are not recommended.[60]
              </p>
            </div>
            <p className="font-medium mb-2" data-id="element-562">Anti-Inflammatory Diet:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-563">
              <li data-id="element-564">Focus on omega-3 fatty acids, polyphenols, fiber</li>
              <li data-id="element-565">
                May help reduce chronic inflammation associated with cancer
                progression
              </li>
            </ul>
            <p className="font-medium mb-2" data-id="element-566">Protein-Focused Approach:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-567">
              <li data-id="element-568">
                Critical for patients experiencing weight loss or cachexia
              </li>
              <li data-id="element-569">
                Work with oncology dietitian for personalized recommendations
              </li>
            </ul>
          </SubSection>
          <SubSection id="exercise" title="4.4 Exercise" data-id="element-570">
            <p className="mb-4" data-id="element-571">
              <strong data-id="element-572">Overview:</strong> Exercise during cancer treatment is
              safe and beneficial for most patients with NSCLC.
            </p>
            <div className="bg-yellow-50 p-4 rounded-lg mb-6" data-id="element-573">
              <h4 className="font-semibold text-yellow-800 mb-2" data-id="element-574">
                Important Clarification on Exercise Recommendations:
              </h4>
              <p data-id="element-575">
                The frequently cited 150 minutes of moderate-intensity aerobic
                activity per week recommendation derives from general cancer
                prevention guidelines (American Cancer Society) and American
                College of Sports Medicine standards, not specifically from
                ASCO/SIO integrative oncology treatment guidelines.[61] The ASCO
                2022 Guideline on Exercise, Diet, and Weight Management During
                Cancer Treatment recommends "regular aerobic and resistance
                exercise during active treatment with curative intent" but does
                not specify exact minute requirements in the guideline
                itself.[62] The 150-minute target appears in patient education
                materials adapted from general population guidelines rather than
                representing a specific evidence-based recommendation from
                integrative oncology clinical practice guidelines.
              </p>
            </div>
            <p className="font-medium mb-2" data-id="element-576">
              General Recommendations for Cancer Treatment:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-577">
              <li data-id="element-578">
                Aerobic activity: Regular moderate-intensity exercise as
                tolerated
              </li>
              <li data-id="element-579">Resistance training: 2-3 times per week</li>
              <li data-id="element-580">Start slowly and increase gradually</li>
            </ul>
            <p className="font-medium mb-2" data-id="element-581">Benefits for NSCLC patients:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-582">
              <li data-id="element-583">Reduced fatigue (most consistent finding)</li>
              <li data-id="element-584">Improved physical function and strength</li>
              <li data-id="element-585">Better quality of life</li>
              <li data-id="element-586">Reduced anxiety and depression</li>
              <li data-id="element-587">May improve treatment tolerance</li>
            </ul>
            <p className="font-medium mb-2" data-id="element-588">Options:</p>
            <p className="font-medium mb-2" data-id="element-589">Aerobic Exercise:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-590">
              <li data-id="element-591">Walking (most accessible)</li>
              <li data-id="element-592">Cycling</li>
              <li data-id="element-593">Swimming or water aerobics (gentle on joints)</li>
              <li data-id="element-594">Tai Chi (combines movement with mindfulness)</li>
            </ul>
            <p className="font-medium mb-2" data-id="element-595">Resistance Training:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-596">
              <li data-id="element-597">Light weights or resistance bands</li>
              <li data-id="element-598">Body weight exercises (modified as needed)</li>
              <li data-id="element-599">Focus on major muscle groups</li>
            </ul>
            <p className="font-medium mb-2" data-id="element-600">Considerations for NSCLC:</p>
            <div className="flex flex-wrap gap-2 mb-6" data-id="element-601">
              <div className="flex items-center" data-id="element-602">
                <input type="checkbox" className="mr-2" data-id="element-603" />
                <span data-id="element-604">Start slowly if sedentary or post-surgery</span>
              </div>
              <div className="flex items-center" data-id="element-605">
                <input type="checkbox" className="mr-2" data-id="element-606" />
                <span data-id="element-607">Monitor for breathlessness; adjust intensity</span>
              </div>
              <div className="flex items-center" data-id="element-608">
                <input type="checkbox" className="mr-2" data-id="element-609" />
                <span data-id="element-610">
                  Coordinate with pulmonary rehabilitation if available
                </span>
              </div>
              <div className="flex items-center" data-id="element-611">
                <input type="checkbox" className="mr-2" data-id="element-612" />
                <span data-id="element-613">Work with cancer exercise specialist when possible</span>
              </div>
            </div>
          </SubSection>
          <SubSection id="stress-reduction" title="4.5 Stress Reduction" data-id="element-614">
            <p className="mb-4" data-id="element-615">
              <strong data-id="element-616">Overview:</strong> Chronic stress can impair immune
              function and increase inflammation. Evidence-based stress
              reduction techniques can improve quality of life and potentially
              treatment outcomes.
            </p>
            <p className="font-medium mb-2" data-id="element-617">Options:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-618">
              <li data-id="element-619">
                Mindfulness-Based Stress Reduction (MBSR): 8-week structured
                program, strong evidence[63]
              </li>
              <li data-id="element-620">Meditation: Daily practice, even 10-15 minutes</li>
              <li data-id="element-621">
                Breathing exercises: Diaphragmatic breathing, 4-7-8 technique
              </li>
              <li data-id="element-622">
                Acupuncture: Reduces anxiety, pain, nausea (strong evidence)
              </li>
              <li data-id="element-623">
                Massage therapy: Reduces pain and anxiety (moderate evidence)
              </li>
            </ul>
          </SubSection>
          <SubSection id="emotional-wellbeing" title="4.6 Emotional Wellbeing" data-id="element-624">
            <p className="mb-4" data-id="element-625">
              <strong data-id="element-626">Overview:</strong> Psychological distress is common in
              NSCLC patients. Professional support can significantly improve
              outcomes.
            </p>
            <p className="font-medium mb-2" data-id="element-627">Options:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-628">
              <li data-id="element-629">
                Individual counseling: Cognitive Behavioral Therapy (CBT) is
                evidence-based
              </li>
              <li data-id="element-630">
                Support groups: In-person or online (LUNGevity, Cancer Support
                Community)
              </li>
              <li data-id="element-631">Peer mentorship: Connect with other lung cancer survivors</li>
              <li data-id="element-632">Music therapy: Reduces anxiety during treatment</li>
              <li data-id="element-633">Art therapy: Expressive outlet for emotions</li>
            </ul>
          </SubSection>
          <SubSection id="social-connection" title="4.7 Social Connection" data-id="element-634">
            <p className="mb-4" data-id="element-635">
              <strong data-id="element-636">Overview:</strong> Social support is associated with
              better outcomes and quality of life in cancer patients.
            </p>
            <p className="font-medium mb-2" data-id="element-637">Options:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-638">
              <li data-id="element-639">
                Join lung cancer-specific support groups (LUNGevity, GO2 for
                Lung Cancer)
              </li>
              <li data-id="element-640">Maintain connections with family and friends</li>
              <li data-id="element-641">Consider pet therapy (reduces stress and isolation)</li>
              <li data-id="element-642">Participate in cancer community events</li>
              <li data-id="element-643">Online communities (Inspire, Smart Patients)</li>
            </ul>
          </SubSection>
          <SubSection id="mindset" title="4.8 Mindset" data-id="element-644">
            <p className="mb-4" data-id="element-645">
              <strong data-id="element-646">Overview:</strong> A positive, engaged mindset doesn't
              mean ignoring difficult emotions, but rather maintaining hope and
              actively participating in your care.
            </p>
            <p className="font-medium mb-2" data-id="element-647">Options:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-648">
              <li data-id="element-649">Goal-setting: Short-term, achievable goals</li>
              <li data-id="element-650">Gratitude practice: Daily journaling</li>
              <li data-id="element-651">Meaning-making: Finding purpose in experiences</li>
              <li data-id="element-652">Stress reframing: Cognitive techniques to manage worry</li>
              <li data-id="element-653">
                Patient empowerment: Taking active role in treatment decisions
              </li>
            </ul>
          </SubSection>
        </GuidelineSection>
      </div>
      <div className="lg:col-span-1" data-id="element-654">
        <div className="lg:sticky lg:top-20" data-id="element-655">
          <TableOfContents items={tocItems} data-id="element-656" />
        </div>
      </div>
    </div>;
};
export default WholeHealth;